import * as React from "react";

import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import blue from "@material-ui/core/colors/blue";
import green from "@material-ui/core/colors/green";
import orange from "@material-ui/core/colors/orange";
import purple from "@material-ui/core/colors/purple";
import red from "@material-ui/core/colors/red";

import { PageWrapper } from "../components/PageWrapper/PageWrapper";
import { ColoredBandContainer } from "../components/PageWrapper/ColoredBandContainer";
import { Container } from "../components/PageWrapper/Container";
import { Iris, IrisEditor } from "../components/Iris";
import { PageHeader } from "../components/PageWrapper/PageHeader";
import { Pricing } from "./PricingPage";
import { SignupButton } from "../components/SignupButton/SignupButton";

import iris_editor_large from "../images/iris_editor_large.png";
import iris_editor_metrics_top from "../images/iris_editor_metrics_sub.png";
import iris_editor_metric_detail from "../images/iris_editor_metric_detail.png";
import iris_editor_histogram from "../images/iris_editor_histogram.png";
import iris_editor_sug_small from "../images/iris_editor_sug_small.png";
import iris_editor_many from "../images/editor_many_large.png";

export const IrisEditorPage = () => {
  return (
    <PageWrapper>
      <Container>
        <Hero />
      </Container>
      <ColoredBandContainer>
        <MainEditor />
      </ColoredBandContainer>
      <Container>
        <Features />
      </Container>
      <ColoredBandContainer dark={true}>
        <GetStarted darkMode={true} />
      </ColoredBandContainer>
      <Container>
        <Box mt={8} mb={16}>
          <PageHeader
            overline="Pricing"
            title="Flexible and affordable"
            subtitle={
              <>
                <Iris /> is ~30x more affordable than traditional manuscript
                assessments.
              </>
            }
          />
          <Pricing canExpand={true} mainPrice="subscription" />
        </Box>
      </Container>
    </PageWrapper>
  );
};

const Hero = () => {
  return (
    <Grid
      container={true}
      spacing={4}
      style={{ marginTop: 16, marginBottom: 32 }}
    >
      <Grid item={true} xs={12} md={6}>
        <Box mt={8} mb={4}>
          <Typography
            variant="h2"
            color="textPrimary"
            variantMapping={{ h2: "h1" }}
            style={{ fontWeight: 700 }}
          >
            Editorial feedback{" "}
          </Typography>
          <Typography
            component="span"
            variant="h2"
            color="primary"
            variantMapping={{ h2: "h1" }}
            style={{
              background: `linear-gradient(180deg, transparent 82%, ${orange[100]} 0%)`,
            }}
          >
            in real time
          </Typography>
        </Box>
        <Box mb={4}>
          <Typography variant="h6" component="p" color="textSecondary">
            Our writing assistant, providing assessments and editorial hints as
            you type - like Grammarly for fiction authors.
          </Typography>
        </Box>
        <Box>
          <SignupButton textAlign="left" place="iris-editor" />
        </Box>
      </Grid>
      <Grid item={true} xs={12} md={6}>
        <Box boxShadow={3} borderRadius={8}>
          <img
            src={iris_editor_large}
            style={{
              width: "100%",
              maxWidth: 800,
            }}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

const MainEditor = () => {
  return (
    <Grid
      container={true}
      spacing={4}
      style={{ marginTop: 16, marginBottom: 32 }}
    >
      <Grid item={true} xs={12} md={6}>
        <Box width="100%" height={400}>
          <img
            src={iris_editor_many}
            width="100%"
            height="100%"
            style={{ borderRadius: 8 }}
          />
        </Box>
      </Grid>
      <Grid item={true} xs={12} md={6}>
        <Box mt={4}>
          <Typography
            variant="h4"
            color="textPrimary"
            gutterBottom={true}
            style={{ fontWeight: 700 }}
          >
            Your writing companion
          </Typography>
          <Typography variant="h6" color="textSecondary">
            <IrisEditor /> is your intelligent writing companion designed
            exclusively for fiction authors. Whether you're drafting your first
            novel or polishing your next bestseller, <Iris /> delivers real-time
            feedback tailored to creative storytelling.
            <br />
            <br />
            It's your secret weapon to write better fiction faster.
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

const Features = () => {
  return (
    <Box mt={8}>
      <PageHeader
        overline="Features"
        title={
          <>
            Discover <IrisEditor />
          </>
        }
      />
      <Grid
        container={true}
        spacing={4}
        style={{ marginTop: 16, marginBottom: 32 }}
      >
        <Grid item={true} xs={12} md={6}>
          <Box bgcolor={blue[50]} borderRadius={8} p={4}>
            <Box textAlign="center">
              <img
                src={iris_editor_metrics_top}
                style={{
                  width: "100%",
                  maxWidth: 400,
                  borderRadius: 4,
                }}
              />
            </Box>
            <Box mt={4}>
              <Typography
                variant="h6"
                color="textPrimary"
                gutterBottom={true}
                style={{ fontWeight: 700 }}
              >
                Objective feedback in real-time
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <IrisEditor /> gives you actionable feedback, specifically tuned
                for fiction writing across 40 genres. Watch the numbers update
                as you type and focus on what you do best - writing!
              </Typography>
            </Box>
          </Box>
          <Box bgcolor={red[50]} borderRadius={8} p={4} mt={4}>
            <Box textAlign="center" mt={6}>
              <img
                src={iris_editor_histogram}
                style={{
                  width: "100%",
                  maxWidth: 400,
                  borderRadius: 4,
                }}
              />
            </Box>
            <Box mt={6}>
              <Typography
                variant="h6"
                color="textPrimary"
                gutterBottom={true}
                style={{ fontWeight: 700 }}
              >
                Genre comparisons
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <Iris />' histograms visualize reader expectations and show you
                where your manuscript stands in its genre.
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item={true} xs={12} md={6}>
          <Box bgcolor={purple[50]} borderRadius={8} p={4} mt={4}>
            <Box textAlign="center" mt={4}>
              <img
                src={iris_editor_sug_small}
                style={{
                  width: "100%",
                  maxWidth: 480,
                  borderRadius: 4,
                }}
              />
            </Box>
            <Box mt={4}>
              <Typography
                variant="h6"
                color="textPrimary"
                gutterBottom={true}
                style={{ fontWeight: 700 }}
              >
                Meaningful editorial suggestions
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Any editor can spot spelling mistakes. <IrisEditor /> gives you
                suggestions that really count! Clichés, show don't tell's,
                inconsistencies... we got you covered!
              </Typography>
            </Box>
          </Box>
          <Box bgcolor={green[50]} borderRadius={8} p={4} mt={4}>
            <Box textAlign="center">
              <img
                src={iris_editor_metric_detail}
                style={{
                  width: "100%",
                  maxWidth: 400,
                  borderRadius: 4,
                }}
              />
            </Box>
            <Box mt={4}>
              <Typography
                variant="h6"
                color="textPrimary"
                gutterBottom={true}
                style={{ fontWeight: 700 }}
              >
                30+ detailed metrics
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <Iris />' feedback stretches across many dimensions: sales
                potential, genre fit, pacing, active voice, auxiliary verbs, ...
                These mentrics paint the hollistic picture of your writing!
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const GetStarted = (props: { darkMode: boolean }) => {
  const { darkMode } = props;

  return (
    <Box my={2}>
      <PageHeader
        overline="Get Started"
        title={
          <>
            Get started with <IrisEditor /> today
          </>
        }
        subtitle={
          <>
            Whether you're drafting your first novel or polishing your next
            bestseller, it's your secret weapon to write better fiction faster.
          </>
        }
        align="center"
        darkMode={darkMode}
      />
      <SignupButton place="iris-editor" />
    </Box>
  );
};

export default IrisEditorPage;



// WEBPACK FOOTER //
// ./src/pages/IrisEditorPage.tsx